import authBrandImage from '../component/auth-modal/images/auth-brand.jpg';
import { getImageAttrs } from '~/modules/images';
import type { GateSubject } from './gate-subject';

/**
 * Chosen by a surface with no item behind it — the plain sign-in modal — where
 * brand imagery is the right answer rather than a consolation.
 */
export const GATE_PANE_BRAND_DEFAULT = 'gate-pane:brand-default';

/**
 * An intent gate that could not find its item's imagery. Renders the same photo
 * as the brand default but is a distinct value, so "we lost the image" can never
 * masquerade as "we chose the default" — see `resolveGatePaneImage`.
 */
export const GATE_PANE_IMAGE_MISSING = 'gate-pane:missing';

export type GatePaneImage =
    | string
    | typeof GATE_PANE_BRAND_DEFAULT
    | typeof GATE_PANE_IMAGE_MISSING;

/** The one place the stock brand photo is read. */
export const gatePaneImageSrc = (image: GatePaneImage): string =>
    image === GATE_PANE_BRAND_DEFAULT || image === GATE_PANE_IMAGE_MISSING
        ? authBrandImage
        : image;

/**
 * The pane's width in the modal at rest — TwoPaneSurface's `w-119.5`.
 * Asking the CDN for that rendition is what keeps a gate from pulling the
 * item's original photograph, which on a project banner is 3200px wide and
 * several megabytes, for a pane 478px across.
 */
const GATE_PANE_WIDTH_PX = 478;

/**
 * The pane image's `<img>` attributes: the CDN's rendition at the pane's
 * width for an item's photograph, and the bundled stock photo untouched —
 * it is already a build asset, and the image service has nothing to serve it
 * from.
 */
export const gatePaneImageAttrs = (image: GatePaneImage) =>
    image === GATE_PANE_BRAND_DEFAULT || image === GATE_PANE_IMAGE_MISSING
        ? { src: authBrandImage }
        : getImageAttrs(image, { width: GATE_PANE_WIDTH_PX });

export const isGatePaneImageMissing = (image: GatePaneImage): boolean =>
    image === GATE_PANE_IMAGE_MISSING;

interface ResolveGatePaneImageOptions {
    /** Imagery the gate was opened with. Most specific, so it wins. */
    override?: string | null;
    /** The item the current page is about. */
    subject?: GateSubject | null;
    /** Names the gate in the development warning. */
    gate: string;
}

/**
 * Resolve the imagery behind an intent gate: what the gate was opened with,
 * then what the page is about, and only then a signal that neither was there.
 *
 * Never returns the brand default. A gate that stands between a reader and a
 * specific item and shows a stock photo instead is a defect, so it reports
 * itself rather than rendering something plausible.
 */
export const resolveGatePaneImage = ({
    override,
    subject,
    gate,
}: ResolveGatePaneImageOptions): GatePaneImage => {
    const resolved = override || subject?.hero || subject?.tile;
    if (resolved) return resolved;

    if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error(
            `[gate-pane-image] "${gate}" has no imagery and fell back to the ` +
                `stock brand photo. Either the surface that opened it should ` +
                `pass an image, or the page should publish a gate subject ` +
                `via useGateSubject(). Page subject: ` +
                `${subject ? `${subject.kind}#${subject.id}` : 'none'}.`,
        );
    }

    return GATE_PANE_IMAGE_MISSING;
};

/**
 * The thumbnail alongside a gate's copy. Prefers the tile rendition, since it is
 * rendered small. Absent imagery renders nothing here rather than a stock photo,
 * which is already how these rows behave.
 */
export const resolveGateThumbnail = ({
    override,
    subject,
}: Omit<ResolveGatePaneImageOptions, 'gate'>): string | undefined =>
    override || subject?.tile || subject?.hero || undefined;
